// src/pages/AboutOverview.jsx
import Section from "../components/Section";
import { Link } from "react-router-dom";

export default function AboutOverview() {
  return (
    <>
      <section className="bg-gradient-to-b from-rose-50 to-white py-16">
        <Section eyebrow="About" title="What is Thalassemia?">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Text */}
            <div className="prose prose-slate max-w-none">
              <p className="text-lg leading-relaxed text-slate-700">
                <strong className="text-rose-600">Thalassemia</strong> is an inherited blood disorder that affects
                <em> haemoglobin</em> in red blood cells (RBCs) that carry oxygen.
              </p>
              <p className="text-slate-600 mt-3">
                Think of RBCs as <span className="italic">“oxygen trucks”</span>. In thalassemia, due to a gene issue,
                these trucks can’t carry oxygen properly — leading to tiredness, weakness, or pale skin.
              </p>

              {/* Types */}
              <div className="mt-6 grid gap-4">
                <Card><b className="text-rose-600">Thalassemia Minor (Carrier):</b> Usually healthy; sometimes mild anaemia.</Card>
                <Card><b className="text-rose-600">Thalassemia Major:</b> Serious; requires regular blood transfusions.</Card>
                <Card><b className="text-rose-600">Intermedia:</b> Between minor and major in severity.</Card>
              </div>

              <Info>It is <u>genetic (inherited)</u> — not contagious.</Info>
            </div>

            {/* Image */}
            <div className="relative">
              <img src="/bloodcells.avif" alt="Blood cells" className="rounded-xl shadow-lg border" />
              <div className="absolute -bottom-4 -left-4 bg-white px-4 py-2 rounded-lg shadow-md text-sm text-slate-600">
                Red Blood Cells are like <span className="font-semibold">oxygen trucks</span>
              </div>
            </div>
          </div>
        </Section>
      </section>

      <Section eyebrow="Cause" title="Why does it happen?">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
            <img src="/dna.png" alt="DNA genetic cause" className="rounded-xl shadow-lg border" />
            <div className="grid grid-cols-2 gap-4">
              <Mini title="Normal Blood" tone="ok">Healthy RBCs with enough haemoglobin.</Mini>
              <Mini title="Thalassemia Blood" tone="danger">Fewer/fragile RBCs that carry less oxygen.</Mini>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-lg leading-relaxed text-slate-700">
              In <strong className="text-rose-600">thalassemia</strong>, the body cannot make proper haemoglobin because of a
              <span className="font-semibold"> genetic mutation</span>.
            </p>
            <p className="mt-3 text-slate-600">
              Imagine a factory making oxygen-carrying “trucks”. In thalassemia, the factory is faulty, so the trucks are
              missing or broken.
            </p>
            <Info>Thalassemia doesn’t happen due to diet/lifestyle — it is <u>inherited through genes</u>.</Info>
          </div>
        </div>
      </Section>

      {/* Quick links to subpages */}
      <Section eyebrow="Explore" title="Learn more">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <LinkCard to="/about/genetics" title="Genetics & Family Journey" />
          <LinkCard to="/about/care" title="Care & Treatment" />
          <LinkCard to="/about/symptoms" title="Symptoms" />
          <LinkCard to="/about/prevention" title="Prevention & Testing" />
        </div>
      </Section>
    </>
  );
}

function Card({ children }) {
  return <div className="p-4 rounded-lg border bg-white shadow-sm hover:shadow-md transition">{children}</div>;
}
function Info({ children }) {
  return (
    <div className="mt-6 p-4 rounded-xl border-l-4 border-emerald-600 bg-emerald-50 text-slate-800">{children}</div>
  );
}
function Mini({ title, tone = "ok", children }) {
  const tones = {
    ok: "text-emerald-600",
    danger: "text-rose-600",
  };
  return (
    <div className="p-4 rounded-lg border bg-white shadow hover:shadow-md transition text-center">
      <h4 className={`font-semibold ${tones[tone] || ""}`}>{title}</h4>
      <p className="text-sm text-slate-600 mt-1">{children}</p>
    </div>
  );
}
function LinkCard({ to, title }) {
  return (
    <Link to={to} className="block rounded-xl border p-5 bg-white shadow-sm hover:shadow-md transition">
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-1">Read more →</p>
    </Link>
  );
}
